import React, { useCallback, useEffect, useState } from 'react';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import {
  fetchDistrictAuthorizationSummary,
  reassignServiceArea,
  createServiceArea,
  fetchServiceAreasByTechService,
  updateServiceArea,
} from '../services/serviceAreasService';
import { accordionSummaryLabel, tekSatirLabel } from '../utils/districtAuthSummary';
import './DistrictAuthorizationInfoModal.css';

const getErrorMessage = (err, fallback) =>
  err?.data?.mesaj || err?.message || err?.mesaj || fallback;

const formatGroupName = (name) => {
  const s = String(name || '—').trim();
  if (!s) return '—';
  if (s === s.toUpperCase() && /[A-ZĞÜŞİÖÇ]/.test(s)) {
    return s.charAt(0) + s.slice(1).toLowerCase();
  }
  return s;
};

const DistrictAuthorizationInfoModal = ({
  isOpen,
  districtId,
  districtName,
  targetTechServiceId,
  cityId,
  onClose,
  onChanged,
}) => {
  const { confirm } = useConfirmDialog();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [kokGruplar, setKokGruplar] = useState([]);
  const [expandedKok, setExpandedKok] = useState({});
  const [submittingKey, setSubmittingKey] = useState(null);

  const loadSummary = useCallback(async () => {
    if (!districtId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchDistrictAuthorizationSummary(districtId);
      setKokGruplar(res?.veri?.kokGruplar || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Özet yüklenemedi'));
      setKokGruplar([]);
    } finally {
      setLoading(false);
    }
  }, [districtId]);

  useEffect(() => {
    if (isOpen && districtId) {
      loadSummary();
      setExpandedKok({});
    }
  }, [isOpen, districtId, loadSummary]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (submittingKey) return;
    onClose?.();
  };

  const assignScope = async ({ leafIds, serviceAreaId, label }) => {
    if (!targetTechServiceId || !cityId || !districtId || !leafIds?.length) return;

    const ok = await confirm({
      title: 'Atama yap',
      message: `${districtName || 'İlçe'} — ${label} yetkisi bu teknik servise verilsin mi?`,
      confirmLabel: 'Atama yap',
      variant: 'warning',
    });
    if (!ok) return;

    const key = `${serviceAreaId || 'new'}-${leafIds.join(',')}`;
    setSubmittingKey(key);
    setError('');
    try {
      if (serviceAreaId && serviceAreaId !== 'self') {
        await reassignServiceArea({
          targetTechServiceId,
          cityId: Number(cityId),
          districtId: Number(districtId),
          serviceAreaId,
          yetkiliKategoriIds: leafIds,
        });
      } else {
        const existingRes = await fetchServiceAreasByTechService(targetTechServiceId);
        const areas = existingRes?.veri?.yetkiAlanlari || [];
        const match = areas.find(
          (a) => Number(a.districtId || a.ilce?.id) === Number(districtId),
        );
        const areaId = match?.id || match?._id;
        if (areaId) {
          const prev = (match.yetkiliKategoriIds || match.yetkiliKategoriler || [])
            .map((k) => String(k?.id || k?._id || k))
            .filter(Boolean);
          const merged = [...new Set([...prev, ...leafIds.map(String)])];
          await updateServiceArea(areaId, { yetkiliKategoriIds: merged });
        } else {
          await createServiceArea({
            techService: targetTechServiceId,
            cityId: Number(cityId),
            districtId: Number(districtId),
            yetkiliKategoriIds: leafIds,
          });
        }
      }
      window.showNotification?.('success', 'Yetki atandı');
      await loadSummary();
      onChanged?.();
    } catch (err) {
      setError(getErrorMessage(err, 'Atama yapılamadı'));
    } finally {
      setSubmittingKey(null);
    }
  };

  const renderStatus = (grup) => {
    if (grup.durum === 'bos') {
      return <span className="district-auth-modal__status district-auth-modal__status--empty">Boş</span>;
    }
    const ad = grup.techService?.ad;
    return (
      <span className="district-auth-modal__status district-auth-modal__status--filled">
        {ad || 'Kısmi yetki'}
      </span>
    );
  };

  const renderAssignButton = (grup, label) => {
    const leafIds = grup.leafIds || [];
    if (!leafIds.length) return null;
    const isTarget =
      grup.techService?.id && String(grup.techService.id) === String(targetTechServiceId);
    if (isTarget && grup.durum === 'yetkili') return null;

    const serviceAreaId = grup.durum === 'bos' ? null : grup.serviceAreaId || null;
    const key = `${serviceAreaId || 'new'}-${leafIds.join(',')}`;
    const busy = submittingKey === key;

    return (
      <button
        type="button"
        className="secondary-btn district-auth-modal__assign"
        disabled={busy || loading}
        onClick={(e) => {
          e.stopPropagation();
          assignScope({ leafIds, serviceAreaId, label });
        }}
      >
        {busy ? 'Kaydediliyor…' : 'Atama yap'}
      </button>
    );
  };

  const renderAltRow = (alt) => (
    <div key={alt.altId} className="district-auth-modal__child">
      <div className="district-auth-modal__child-body">
        <div className="district-auth-modal__child-name">{formatGroupName(alt.altAd)}</div>
        {renderStatus(alt)}
      </div>
      {renderAssignButton(alt, alt.altAd)}
    </div>
  );

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={handleClose}>
      <div
        className="modal-content district-auth-modal"
        style={{ maxWidth: 540, width: '92vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{districtName || 'İlçe'} — yetki özeti</h2>
          <button type="button" className="modal-close-btn" onClick={handleClose} aria-label="Kapat">
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-gray)' }}>
              Yükleniyor…
            </div>
          ) : (
            <div className="district-auth-modal__list">
              {kokGruplar.map((kok) => {
                const isAcc = kok.gorunum === 'akordeon';
                const open = !!expandedKok[kok.kokId];
                const toggle = () =>
                  setExpandedKok((p) => ({ ...p, [kok.kokId]: !p[kok.kokId] }));

                return (
                  <div key={kok.kokId} className="district-auth-modal__group">
                    {isAcc ? (
                      <button
                        type="button"
                        className="district-auth-modal__group-header district-auth-modal__group-header--accordion"
                        aria-expanded={open}
                        onClick={toggle}
                      >
                        <span className="district-auth-modal__chevron" aria-hidden="true">
                          <i className={`fas fa-chevron-${open ? 'down' : 'right'}`} />
                        </span>
                        <div className="district-auth-modal__group-title">
                          <div className="district-auth-modal__group-name">
                            {formatGroupName(kok.kokAd)}
                          </div>
                          <div className="district-auth-modal__group-meta">
                            {accordionSummaryLabel(kok.yetkiliAlt, kok.bosAlt)}
                          </div>
                        </div>
                      </button>
                    ) : (
                      <div className="district-auth-modal__group-header">
                        <span className="district-auth-modal__chevron" aria-hidden="true" />
                        <div className="district-auth-modal__group-title">
                          <div className="district-auth-modal__group-name">
                            {formatGroupName(kok.kokAd)}
                          </div>
                          <div className="district-auth-modal__group-meta">{tekSatirLabel(kok)}</div>
                        </div>
                        {renderAssignButton(kok, kok.kokAd)}
                      </div>
                    )}
                    {isAcc && open && (
                      <div className="district-auth-modal__children" role="group">
                        {(kok.altGruplar || []).map(renderAltRow)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {error && (
            <div style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{error}</div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="secondary-btn" onClick={handleClose} disabled={!!submittingKey}>
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default DistrictAuthorizationInfoModal;
